// src/store/modules/auth.js
import {authMe} from "@/api/ReplySetting"
import { getAuthEntities, clearAuthEntitiesCache } from "@/api/information.js"
import { renameDemoBrand } from "@/config/demoAccount"
import { normalizeClusters, findCluster } from "@/utils/cluster"
import { identifyLibredesk, signOutLibredesk } from "@/utils/libredesk.js"
import { identifyUser, setCurrentUnit, resetAnalytics } from "@/utils/analytics"

// 同一次登录会话内缓存 /auth/entities 的请求，避免 Layout / RouterMenu / HotelSelector
// 等多处几乎同时挂载时各自发起一次请求。登出或重新登录时清空。
let entitiesPromise = null;

export default {
  namespaced: true,
  state: {
    token: null,         // Token状态
    email: null,
    userInfo: null,      // 用户信息
    isAuthenticated: false, // 认证状态
    // 品牌模式可选的 cluster 列表（/auth/entities 的 clusters，见 utils/cluster.js）和当前选中的
    // cluster 名。字段名沿用原来的 brands / selectedBrandName（2026-10-30 前存的是 /auth/entities
    // 的 brands），侧栏、选择页按 .name / .hotelGroup 读，不用跟着改
    brands: [],
    selectedBrandName: null,
    // Group report's escalation_summary.failure_labels — which finding summaries
    // count as "Critical" this period. Brand-level pages (e.g. BrandOverview3) call
    // a brand-scoped report endpoint that doesn't carry this field, so it's stashed
    // here when navigating over from the group report so isCritical still works.
    criticalFailureLabels: [],
  },
  mutations: {
    SET_EMAIL(state, email) {
      state.email = email;
    },
    SET_TOKEN(state, token) {
      state.token = token;
      state.isAuthenticated = !!token;
    },
    SET_USER_INFO(state, userInfo) {
      state.userInfo = userInfo;
    },
    CLEAR_AUTH(state) {
      state.token = null;
      state.email = null;
      state.userInfo = null;
      state.isAuthenticated = false;
      state.brands = [];
      state.selectedBrandName = null;
    },
    SET_BRANDS(state, list) {
      state.brands = Array.isArray(list) ? list : [];
    },
    SET_SELECTED_BRAND(state, name) {
      state.selectedBrandName = name;
    },
    SET_CRITICAL_FAILURE_LABELS(state, labels) {
      state.criticalFailureLabels = Array.isArray(labels) ? labels : [];
    }
  },
  actions: {
    // params: 当前实体（见 utils/libredeskEntity.js），api 据此把客户所在的酒店/餐厅/品牌写进客服系统。
    // identify: 是否用返回的 token 把挂件升级成登录用户。只有每次进入 Layout 的第一次要做 ——
    // 挂件每收到一个 token 都会清空并重拉会话列表、把正在看的会话换成最新一条；而切店只需要
    // api 在服务端重写属性，身份（同一个 email）不变。
    me({ commit }, { params, identify = true } = {}) {
      return authMe(params).then(res => {
        commit('SET_EMAIL', res.email);
        // 客服挂件的 JWT 只有这个接口会下发。挂件在 App 启动时已经以游客身份挂上,
        // 这里只是把它升级成登录用户。libredeskToken 为 null 不是错误 —— 后端没给
        // 该用户签发,保持游客态即可。
        if (identify) identifyLibredesk(res && res.libredeskToken);
        // PostHog (WTK-728): who is signed in, and which property they are on, for feature flags.
        if (identify) identifyUser(res);
        setCurrentUnit(params);
        return res;
      });
    },
    // 合并后的酒店 / 餐厅 / 品牌列表拉取（GET /auth/entities）
    // 原来分别是 hotel/fetchHotelList（/hotel/dashboard/hotels）
    // 和 restaurant/fetchRestaurantList（/restaurant/dashboard/restaurants）两个接口
    // 同一次登录会话内只会真正请求一次：多个组件几乎同时调用时共用同一个 in-flight
    // Promise；请求成功后缓存下来，之后的调用直接复用结果，不会再打接口。
    // 传 { force: true } 可强制重新拉取（目前没有调用方需要，预留）。
    fetchEntities({ commit, state, rootState, dispatch }, { force = false } = {}) {
      if (entitiesPromise && !force) {
        return entitiesPromise;
      }
      entitiesPromise = getAuthEntities()
        .then((res) => {
          const hotels = (res && res.hotels) || [];
          const restaurants = (res && res.restaurants) || [];
          // 品牌模式的权限来源是 clusters（2026-10-30 起，不再用 brands，见 utils/cluster.js）。
          // 演示品牌在这里改名（Hilton → Grand Luxury，见 config/demoAccount.js）。
          // 放在入口统一改，下游按 name 显示/匹配的地方（品牌选择器、登录页品牌列表、
          // libredeskEntity、BrandOverview2 的 isMockGroupHotel）就都不用各自认旧名。
          // hotelGroup 刻意不改 —— 切品牌时要拿它去 hotelList 里全等匹配一家酒店，改了会匹配不上。
          const clusters = normalizeClusters(res && res.clusters, renameDemoBrand);
          commit("hotel/SET_HOTEL_LIST", hotels, { root: true });
          commit("hotel/SET_HAS_RESTAURANT", restaurants.length > 0, { root: true });
          commit("restaurant/SET_RESTAURANT_LIST", restaurants, { root: true });
          commit("SET_BRANDS", clusters);
          // selectedBrandName 是持久化的（见 store/index.js 的 paths）：老用户的 localStorage 里
          // 可能还是演示品牌的旧名，先跟着改名；改完还对不上任何 cluster（比如存的是改用 cluster
          // 之前的品牌名，或这个 cluster 已经不给这个账号了）就清掉，免得 isBrandMode 被一个
          // 已经没权限的名字撑着、品牌报告接口按它去取一直 403
          const renamedSelected = renameDemoBrand(state.selectedBrandName);
          const keptSelected = renamedSelected && findCluster(clusters, renamedSelected) ? renamedSelected : null;
          if (keptSelected !== state.selectedBrandName) {
            commit("SET_SELECTED_BRAND", keptSelected);
          }
          // 若持久化的 selectedRestaurantId 已失效，回退到第一个餐厅（原 restaurant/fetchRestaurantList 中的逻辑）
          if (restaurants.length) {
            const currentId = rootState.restaurant.selectedRestaurantId;
            const isValid = restaurants.some((r) => r.restaurantId === currentId);
            if (!isValid) {
              commit("restaurant/SET_selectedRestaurantId", restaurants[0].restaurantId, { root: true });
            }
          }
          return res;
        })
        .catch((error) => {
          console.error("Failed to fetch /auth/entities:", error);
          commit("hotel/SET_HOTEL_LIST", [], { root: true });
          commit("restaurant/SET_RESTAURANT_LIST", [], { root: true });
          commit("SET_BRANDS", []);
          dispatch(
            "app/setNotification",
            {
              type: "error",
              message: "Failed to obtain the hotel list. Please try again",
            },
            { root: true }
          );
          // 失败不缓存，允许下次调用重新请求
          entitiesPromise = null;
        });
      return entitiesPromise;
    },
    // 登录时存储Token
    login({ commit }, { token, rememberMe, userInfo }) {
      // 清掉上一次登录（可能是同一台设备上的另一个账号，或本账号之前用过品牌模式）
      // 残留下来的 selectedBrandName——它会被 vuex-persistedstate 存进这台设备的
      // localStorage，一直留到下次登录。不清空的话，HotelSelector.vue 里
      // auth/isBrandMode 的判断会被这个陈旧标记劫持，导致新登录明明只有一家酒店/
      // 没有品牌权限，也被错误地跳去品牌总览页，而不是自动进入酒店页面。
      commit("SET_SELECTED_BRAND", null);
      // 同理清空 /auth/entities 的请求缓存，确保这次登录一定会重新拉取最新的
      // 酒店/餐厅/品牌列表，而不是复用上一个账号（同一 SPA 会话内未刷新页面时）缓存的结果。
      entitiesPromise = null;
      commit("SET_TOKEN", token);
      if (userInfo) commit("SET_USER_INFO", userInfo);

      // Clear both storages first to prevent stale token conflicts
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
      if (rememberMe) {
        localStorage.setItem("token", token);
      } else {
        sessionStorage.setItem("token", token);
      }
    },
    
    // 应用启动时从本地存储恢复Token
    restoreToken({ commit }) {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (token) {
        commit("SET_TOKEN", token);
        return true;
      }
      return false;
    },
    
    // 退出登录
    logout({ commit }) {
      // 客服挂件退回游客态(清 session/visitor cookie),而不是销毁 ——
      // 登录页仍然要有客服入口。这一步同时避免下一个登录的人接着用上一个用户的会话。
      signOutLibredesk();
      resetAnalytics();
      commit("CLEAR_AUTH");
      clearAuthEntitiesCache();
      commit("SET_BRANDS", []);
      commit("SET_SELECTED_BRAND", null);
      commit("hotel/SET_HOTEL_LIST", [], { root: true });
      commit("restaurant/SET_RESTAURANT_LIST", [], { root: true });
      entitiesPromise = null;
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    },

    // 纯品牌账号自动识别：/auth/entities 返回的酒店和餐厅都为空、但 cluster 列表不为空时，
    // 说明这是一个纯品牌账号，跳过手动选择步骤直接进入品牌模式。多个 cluster 时先默认选中
    // 第一个——这里只负责识别和存状态。
    async detectBrandOnlyMode({ commit }) {
      let res;
      try {
        res = await getAuthEntities();
      } catch (e) {
        return;
      }
      const hotels = (res && res.hotels) || [];
      const restaurants = (res && res.restaurants) || [];
      // 和 fetchEntities 一样取 clusters、过一遍演示品牌改名：这个 action 每次进 Layout 都会跑
      // (layout/index.vue)，它这一句 SET_BRANDS 会盖掉 fetchEntities 已经整理好的列表，
      // 不改就会把左上角的名字又打回旧名；纯品牌账号下面那句 SET_SELECTED_BRAND 取的
      // 也得是新名，否则和改名后的列表对不上。
      const clusters = normalizeClusters(res && res.clusters, renameDemoBrand);
      commit("SET_BRANDS", clusters);
      if (!hotels.length && !restaurants.length && clusters.length) {
        commit("SET_SELECTED_BRAND", clusters[0].name);
      }
    },
  },
  getters: {
    getToken: state => state.token,
    getUserInfo: state => state.userInfo,
    isAuthenticated: state => state.isAuthenticated,
    // /auth/entities 只有 cluster（没有酒店/餐厅）、或用户手动选了品牌时为 true。
    // 登录/选择页走了 Brand tab（HotelSelector.vue 的 auth/SET_SELECTED_BRAND）才为 true。
    isBrandMode: state => !!state.selectedBrandName,
    // 品牌模式下选中的 cluster（{ name, hotelGroup, hotels, brands, … }）；不在品牌模式时为 null
    selectedCluster: state => findCluster(state.brands, state.selectedBrandName),
  }
};